import { useCallback, useEffect, useRef, useState } from 'react';
import type { IMonitorReplay, IReplayEvent } from '@interfaces/IMonitorApi';
import type { IReplayView } from './buildReplayView';
import { dayScale } from './trackScale';

/** The day on screen when the user left it: shown dimmed until the next day loads. */
export interface IHeldDay {
  replay: IMonitorReplay;
  /** Its playhead (the data end when it was live). */
  atMs: number;
}

interface IHeld {
  day: IHeldDay;
  /** Its building frame, without the toasts; null when it was live. */
  view: IReplayView | null;
}

/** What is on screen at the last render, for `hold` to read from a handler. */
interface IOnScreen {
  replay: IMonitorReplay | undefined;
  atMs: number | null;
  view: IReplayView | null;
}

const NO_EVENTS: readonly IReplayEvent[] = [];

/**
 * Keeps the day on screen while the next one loads. `hold` snapshots it — call it
 * from the handler that leaves the day (another pick, or LIVE). The snapshot is
 * released once another day is in (at once when it was cached), or when `keep`
 * turns false (the bar closed, or the load failed). It is not released on "not
 * loading": the URL change that starts the load lands a render after `hold`.
 */
export function useHeldDay(replay: IMonitorReplay | undefined, atMs: number | null, view: IReplayView | null, keep: boolean) {
  const onScreen = useRef<IOnScreen>({ replay, atMs, view });
  useEffect(() => {
    onScreen.current = { replay, atMs, view };
  }, [replay, atMs, view]);
  const [held, setHeld] = useState<IHeld | null>(null);
  if (held && (!keep || (replay !== undefined && replay !== held.day.replay))) setHeld(null);
  const hold = useCallback(() => {
    const { replay: day, atMs: dayAtMs, view: dayView } = onScreen.current;
    if (!day) return;
    setHeld({
      day: { replay: day, atMs: dayAtMs ?? dayScale(day).dataEnd },
      view: dayView && { ...dayView, activeEvents: NO_EVENTS },
    });
  }, []);
  return { held, hold };
}
